
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Trophy, Star, BookOpen, Target, Flame, Award } from 'lucide-react';
import { useUserGamification, useUserAchievements } from '@/hooks/useUserProgress';

const UserProgressDisplay: React.FC = () => {
  const { data: gamificationData } = useUserGamification();
  const { data: achievements } = useUserAchievements();

  if (!gamificationData) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-black text-center">
            Start exploring content to track your progress!
          </p>
        </CardContent>
      </Card>
    );
  }

  const progressToNextLevel = ((gamificationData.total_points % 100) / 100) * 100;
  
  const achievementTypes = {
    'first_view': { icon: BookOpen, label: 'First View', color: 'bg-white0' },
    'first_completion': { icon: Target, label: 'First Completion', color: 'bg-white0' },
    'discipline_explorer': { icon: Star, label: 'Discipline Explorer', color: 'bg-white0' },
    'active_learner': { icon: Flame, label: 'Active Learner', color: 'bg-white0' },
    'knowledge_seeker': { icon: Award, label: 'Knowledge Seeker', color: 'bg-white0' },
    'content_creator': { icon: Trophy, label: 'Content Creator', color: 'bg-indigo-500' },
    'community_helper': { icon: Trophy, label: 'Community Helper', color: 'bg-pink-500' }
  };

  return (
    <div className="space-y-6">
      {/* Level and Points */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Your Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold">Level {gamificationData.level}</p>
              <p className="text-black">{gamificationData.total_points} points</p>
            </div>
            <Badge variant="secondary" className="text-lg px-3 py-1">
              Level {gamificationData.level}
            </Badge>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress to Level {gamificationData.level + 1}</span>
              <span>{gamificationData.total_points % 100}/100</span>
            </div>
            <Progress value={progressToNextLevel} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <BookOpen className="h-6 w-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{gamificationData.nodes_viewed}</p>
              <p className="text-sm text-black">Nodes Viewed</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <Target className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold">{gamificationData.nodes_completed}</p>
              <p className="text-sm text-black">Completed</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <Flame className="h-6 w-6 mx-auto mb-2 text-orange-500" />
              <p className="text-2xl font-bold">{gamificationData.current_streak}</p>
              <p className="text-sm text-black">Day Streak</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <Award className="h-6 w-6 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold">{achievements?.length || 0}</p>
              <p className="text-sm text-black">Achievements</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Achievements */}
      {achievements && achievements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Recent Achievements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {achievements.slice(0, 5).map((achievement) => {
                const achievementInfo = achievementTypes[achievement.achievement_type as keyof typeof achievementTypes];
                const Icon = achievementInfo?.icon || Award;
                
                return (
                  <div key={achievement.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className={`p-2 rounded-full ${achievementInfo?.color || 'bg-white0'}`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{achievementInfo?.label || achievement.achievement_type}</p>
                      <p className="text-sm text-black">
                        Earned {new Date(achievement.earned_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant="outline">+{achievement.points_awarded} pts</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserProgressDisplay;
